/* eslint-disable id-denylist */

// NOTE: Implementation assumes that icon of name 'foo' will be placed in file './icons/Foo.vue'

import { getCssCustomPropertyValue } from '../../helpers/cssCustomProperties';

const APP_SVG_ICON_AMENITIES_TYPE = [
  'adultsOnly',
  'airConditioning',
  'allInclusive',
  'balcony',
  'bar',
  'boutique',
  'carpet',
  'case',
  'castle',
  'ceilingFan',
  'childBeds',
  'childChairs',
  'coffeeMaker',
  'conferenceRoom',
  'desk',
  'dishwasher',
  'electricCarCharger',
  'engineHeater',
  'family',
  'fireplace',
  'freezer',
  'garage',
  'garden',
  'gym',
  'hairDryer',
  'internet',
  'iron',
  'jacuzzi',
  'kettle',
  'lakeView',
  'microwave',
  'miniBar',
  'mountainView',
  'noPets',
  'noSmoking',
  'oven',
  'parking',
  'pets',
  'phone',
  'pool',
  'route',
  'safetyBox',
  'satelliteTv',
  'sauna',
  'save',
  'seaView',
  'shower',
  'shower',
  'ski',
  'smoking',
  'spa',
  'sun',
  'sunbeds',
  'sustainable',
  'tennis',
  'terrace',
  'toaster',
  'tune',
  'tv',
  'vineyard',
  'washingMachine',
] as const;

export const APP_SVG_ICON_TYPE = [
  'accommodation',
  'activity',
  'activityOutlined',
  'ai',
  'airplane',
  'airplaneLanding',
  'airplaneTakeoff',
  'arrowLeft',
  'arrowRight',
  'arrowUpDouble',
  'bagCabin',
  'bagChecked',
  'bagUnavailable',
  'bed',
  'bedOutlined',
  'bedOutlinedCrossed',
  'building',
  'calendarOutlined',
  'car',
  'carOutlined',
  'cart',
  'chair',
  'change',
  'check',
  'chevronDown',
  'chevronLeft',
  'chevronRight',
  'chevronUp',
  'clockOutlined',
  'close',
  'closeInCircle',
  'code',
  'concierge',
  'creditCard',
  'distance',
  'door',
  'drag',
  'eye',
  'ferry',
  'fileList',
  'filter',
  'filterAlt',
  'food',
  'gallery',
  'golf',
  'halfStar',
  'heart',
  'heartOutlined',
  'homeOutlined',
  'infoCircle',
  'itinerary',
  'layersOutlined',
  'lightbulb',
  'link',
  'list',
  'locationMark',
  'locationMarkOutlined',
  'lockClosed',
  'lockOpen',
  'lookingGlass',
  'lowPriceCalendar',
  'map',
  'mapFilled',
  'masonryGallery',
  'minus',
  'money',
  'moon',
  'mop',
  'nearMe',
  'nearMeOutlined',
  'neighbourhood',
  'newTab',
  'noTransport',
  'oneWayFlight',
  'pencil',
  'people',
  'peopleOutlined',
  'personBicycling',
  'personWalking',
  'plus',
  'position',
  'priceLabel',
  'printer',
  'quality',
  'returnFlight',
  'seat',
  'seatInCircle',
  'share',
  'sofa',
  'sort',
  'star',
  'threeDotsHorizontal',
  'searchOff',
  'threeDotsVertical',
  'thumbUp',
  'ticket',
  'trashCanEmpty',
  'trashCanOutlined',
  'unavailable',
  'warningCircle',
  'zoom',
  ...APP_SVG_ICON_AMENITIES_TYPE,
] as const;

export type AppSvgIconType = (typeof APP_SVG_ICON_TYPE)[number];

const STROKE_BASED_ICONS: AppSvgIconType[] = [
  'carpet',
  'adultsOnly',
  'seatInCircle',
];

// NOTE:
// currentColor is CSS currentColor, so icon will have color of surrounding text.
export const APP_SVG_ICON_COLOR = [
  'currentColor',
  'color-white',
  'color-soft-black',
  'color-neutral-70',
  'color-soft-black',
  'color-neutral-70',
  'color-neutral-70',
  'color-neutral-60',
  'color-neutral-40',
  'color-neutral-30',
  'color-brand-summer',
  'color-brand-coral',
  'color-brand-violet',
  'color-brand-jungle',
] as const;

export type AppSvgIconColor = (typeof APP_SVG_ICON_COLOR)[number];

let APP_SVG_ICON_COLOR_MAP: Record<AppSvgIconColor, string> | null = null;

function getAppSvgIconColorMap(): Record<AppSvgIconColor, string> {
  if (APP_SVG_ICON_COLOR_MAP) {
    return APP_SVG_ICON_COLOR_MAP;
  }

  const map = {
    currentColor: 'currentColor',
    'color-white': getCssCustomPropertyValue('color-white'),
    'color-soft-black': getCssCustomPropertyValue('color-soft-black'),
    'color-neutral-70': getCssCustomPropertyValue('color-neutral-70'),
    'color-neutral-60': getCssCustomPropertyValue('color-neutral-60'),
    'color-neutral-40': getCssCustomPropertyValue('color-neutral-40'),
    'color-neutral-30': getCssCustomPropertyValue('color-neutral-30'),
    'color-brand-summer': getCssCustomPropertyValue('color-brand-summer'),
    'color-brand-coral': getCssCustomPropertyValue('color-brand-coral'),
    'color-brand-violet': getCssCustomPropertyValue('color-brand-violet'),
    'color-brand-jungle': getCssCustomPropertyValue('color-brand-jungle'),
  };

  APP_SVG_ICON_COLOR_MAP = map;

  return map;
}

export interface AppSvgIconTranslate {
  x?: number;
  y?: number;
  unit?: 'px';
}
export interface AppSvgIconRotate {
  deg?: number;
  unit?: 'deg';
}

//NOTE: 9, 13 and 16px are just temporary until UX defines standard sizes for small icons -> AVOID USING THEM
export const APP_SVG_ICON_SIZE = [
  '9',
  '13',
  '16',
  '18',
  '24',
  '36',
  '48',
] as const;

export type AppSvgIconSize = (typeof APP_SVG_ICON_SIZE)[number];

export interface AppSvgIconTransformedProps {
  width: string;
  height: string;
  viewBox: string;
  style: {
    // NOTE: without display: block on svg wrapping div is a bit larger,
    // which break alignment in general
    display: 'block';
    stroke?: string;
    fill?: string;
    translate?: string;
    rotate?: string;
  };
}

export function transformPropsFormat(
  name: AppSvgIconType,
  translate?: AppSvgIconTranslate,
  rotate?: AppSvgIconRotate,
  size?: AppSvgIconSize,
  color?: AppSvgIconColor
): AppSvgIconTransformedProps {
  const defaultIconSize: AppSvgIconSize = '24';
  const defaultIconColor: AppSvgIconColor = 'currentColor';
  const iconSize = size || defaultIconSize;
  const translateUnit = translate?.unit ?? 'px';
  const rotateUnit = rotate?.unit ?? 'deg';
  const x = translate?.x ?? 0;
  const y = translate?.y ?? 0;
  const translateStyle =
    x || y
      ? { translate: `${x}${translateUnit} ${y}${translateUnit}` }
      : undefined;
  const rotateStyle = rotate?.deg
    ? { rotate: `${rotate.deg}${rotateUnit}` }
    : undefined;
  const iconColor: string = getAppSvgIconColorMap()[color ?? defaultIconColor];

  const propertyType = STROKE_BASED_ICONS.includes(name) ? 'stroke' : 'fill';
  const style = {
    [propertyType]: iconColor,
    ...(!!translateStyle && translateStyle),
    ...(!!rotateStyle && rotateStyle),
    display: 'block' as const,
  };

  return {
    width: `${iconSize}px`,
    height: `${iconSize}px`,
    viewBox: `0 0 ${defaultIconSize} ${defaultIconSize}`,
    style,
  };
}
